import {Component, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { ADMIN_MODULE, ADMIN_ROL, REPORTS_MODULE, VALIDATIONS_PROCESS_MODULE } from '@constants/frontend';
import { Numbers } from '@constants/numbers';
import { LoginFacadeService } from '@modules/autenticacion/login/store/login.facade.service';
import { ApiHelperService } from '@services/apihelper/api-helper.service';
import { BcsModalComponent } from '@shared/components/bcs-modal/bcs-modal.component';
import { interval, Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { BreadcrumbService } from '@services/breadcrumb/breadcrumb.service';
import { AuthenticationService } from "@services/authentication/authentication.service";
import { SpinnerService } from "@services/utils/spinner.service";
import { BcAlertComponent } from "@bancolombia/design-system-web/bc-alert";
import { BcDialogService } from "@bancolombia/design-system-web/bc-services";
import { HttpErrorResponse } from "@angular/common/http";
import { RolState } from "@modules/autenticacion/login/store/reducers";

interface IBreadcrumbItem {
  name: string;
  url: string;
}

@Component({
  selector: 'app-master',
  templateUrl: './master.component.html',
  styleUrls: ['./master.component.scss'],
})
export class MasterComponent implements OnInit, OnDestroy {
  private destroySubs$: Subject<void> = new Subject<void>();
  @ViewChild('modalErrorHelper') modalErrorHelper!: BcsModalComponent;
  helper: any;
  adminModule: boolean = false;
  VALIDATIONS_PROCESS_MODULE = VALIDATIONS_PROCESS_MODULE;
  ADMIN_MODULE_PARAM = ADMIN_MODULE;
  REPORTS_MODULE = REPORTS_MODULE;
  today = new Date();
  date = this.today.toLocaleDateString('es-us', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  timer = Numbers._30;
  currentDateTime: string = '';

  data: any;
  routerSubscription: Subscription;
  @ViewChild('modalRefreshToken') modalRefreshToken!: BcsModalComponent;
  isModalRefreshTokenOpen = false;
  disabledMenuItem = false;
  hiddenModules = true;
  errorMessage = 'Error inesperado ¡Por favor intente de nuevo!';
  //TIMER
  title = '';
  countdownInactividad: number = Numbers._1140; // 1140 segundos = 19 minutos
  countdownSesion: number = Numbers._1170; // 1170 segundos = 19.5 minutos
  countdownConfirmation: number = Numbers._30;
  interval: any;
  interval2: any;
  interval3: any;
  userActive: boolean = true;
  messageModalRefresh = 'Su sesión está apunto de terminar.';
  userName = '';
  breadcrumbItems: IBreadcrumbItem[] = [];
  isAdminModules: boolean = true;

  constructor(
    public authService: MsalService,
    private loginFacadeService: LoginFacadeService,
    private router: Router,
    private apiHelperService: ApiHelperService,
    private breadcrumbService: BreadcrumbService,
    private authenticationService: AuthenticationService,
    private dialogService: BcDialogService,
    public spinnerService: SpinnerService
  ) {
    this.routerSubscription = new Subscription();
    this.handleErrorApiHelper();
    this.startCountdownInactividad();
    this.startCountdownLogOut();
  }

  ngOnInit(): void {
    this.initParameters();
    this.subscribeToRouterEvents();
    this.reloj();
    this.getBreadCrumb();
  }

  getBreadCrumb(): void {
    this.breadcrumbService
      .generateBreadcrumbs()
      .pipe(takeUntil(this.destroySubs$))
      .subscribe((breadcrumbs) => {
        this.breadcrumbItems = breadcrumbs;
      });
  }

  startCountdownLogOut() {
    clearInterval(this.interval2);
    const sessionDuration = Numbers._3200 * Numbers._1000; // en ms
    const startTime = Date.now();
    this.interval2 = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(Numbers._0, sessionDuration - elapsed);
      this.countdownSesion = Math.floor(remaining / Numbers._1000);
      if (remaining <= Numbers._0) {
        this.spinnerService.show();
        this.authenticationService.acquireTokenSilent();
        setTimeout(()=> {
          this.spinnerService.hide();
          this.startCountdownLogOut();
        }, Numbers._365);
      }
    }, Numbers._1000);
  }

  startCountdownInactividad() {
    // Limpia cualquier intervalo anterior
    clearInterval(this.interval);
    this.countdownInactividad = Numbers._1140;
    this.interval = setInterval(() => {
      if (!this.userActive) {
        this.countdownInactividad--;
        if (this.countdownInactividad === Numbers._0) {
          this.modalRefreshToken.closeModal();
          clearInterval(this.interval);
          const message = 'No hemos detectado interacción.';
          this.startCountdownConfirmation(message);
        }
      } else {
        this.resetCountdown();
      }
    }, Numbers._1000);
  }

  startCountdownConfirmation(message: string) {
    // Limpia cualquier intervalo anterior
    clearInterval(this.interval3);
    this.messageModalRefresh = message;
    this.modalRefreshToken.openModal();
    this.countdownConfirmation = Numbers._30;
    this.interval3 = setInterval(() => {
      this.countdownConfirmation--;
      if (this.countdownConfirmation === Numbers._0) {
        clearInterval(this.interval3);
        this.modalRefreshToken.closeModal();
        this.logOut();
      }
    }, Numbers._1000);
  }

  resetCountdown() {
    this.countdownInactividad = Numbers._1140;
    this.userActive = false;
  }

  /* el metodo onUserActivity es el
   * manejador para ambos eventos: mousemove y keydown
   */
  @HostListener('document:mousemove', ['$event'])
  @HostListener('document:keydown', ['$event'])
  onUserActivity(event: Event) {
    if (event) {
      this.userActive = true;
    }
  }

  getRolSubscription = (rol: RolState ) => {
    if (rol.rolName != null) {
      this.userActive = false;
      this.isAdminModules = rol.rolName?.toLowerCase() === ADMIN_ROL.toLowerCase();
      const nameSections = this.authenticationService.getName().split(" ");
      if (nameSections.length > Numbers._0) {
        this.userName = nameSections[Numbers._0];
      }
    } else {
      this.logOut();
    }
  }

  initParameters = () => {
    this.loginFacadeService.getRol().subscribe({
      next: this.getRolSubscription
    })
  };

  /**
   * Inicializa la suscripción a los eventos del router
   * para manejar la habilitación/deshabilitación del menú
   */

  subscribeToRouterEvents(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd || event instanceof NavigationError))
      .subscribe((event: any) => {
         if (event instanceof NavigationError) {
          // Detecta errores de carga de módulos o chunks
          if (event.error && event.error.message && event.error.message.includes('Loading chunk')) {
            this.logOut();
          }
        }
      });
  }

  reloj() {
    interval(Numbers._1000).subscribe(() => {
      const now = new Date();
      this.currentDateTime = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    });
  }


  /**
   * Cambia la página según el módulo seleccionado.
   *
   * - Limpia datos almacenados previamente.
   * - Verifica si el módulo es válido y obtiene su ruta.
   * - Obtiene las pantallas asociadas al módulo desde un `Map` para optimizar la búsqueda.
   * - Actualiza la navegación y reinicia el estado del menú.
   *
   * @param module Nombre del módulo al que se desea navegar.
   */
  changePage(module: string): void {

    const moduleRoutes: { [key: string]: string } = {
      [ADMIN_MODULE]: '/app/administracion',
      [VALIDATIONS_PROCESS_MODULE]: '/app/procesos-validaciones',
      [REPORTS_MODULE]: '/app/reportes',
    };

    // Si el módulo no es válido, redirige a la página principal
    if (!module || !moduleRoutes[module]) {
      this.router.navigate(['app']);
      return;
    }

    this.router.navigate([moduleRoutes[module]]);
  }

  checkTokenAction(event: number): void {
    // Cerrar el modal
    this.modalRefreshToken.closeModal();
    this.isModalRefreshTokenOpen = false;

    // Resetear countdownConfirmation y intervalos
    clearInterval(this.interval3);
    this.countdownConfirmation = Numbers._30;

    if (event === Numbers._0) {
      // Si el evento es 0, cerrar sesión
      this.authService.logout();
      return;
    }
    this.startCountdownInactividad();
  }

  validateIfExist(listModule: any): boolean {
    return listModule != null;
  }

  handleErrorApiHelper(): void {
    this.apiHelperService
      .getErrorSubject()
      .pipe(takeUntil(this.destroySubs$))
      .subscribe((error: HttpErrorResponse | any) => {
        if (error?.message?.includes('Unauthorized')) {
          this.hiddenModules = false;
          this.errorMessage = 'Su sesión ha expirado por favor ingrese de nuevo';
          this.modalErrorHelper.openModal();
          return;
        }
        if (error?.statusText?.includes('Unknown Error')) {
          this.hiddenModules = false;
          this.errorMessage = error?.statusCode !== 409
            ? 'Su sesión será cerrada, <br/> si persiste el error, comunicarse con el administrador'
            : 'Se ha detectado un nuevo inicio de sesión. <br/> Su sesión en este dispositivo será cerrada.';
          this.modalErrorHelper.openModal();
        } else {
          let textAlert =  error.customMessage ?? 'Error de conexión';
          if (typeof error.error === 'string') {
            textAlert = error.error;
          }
          this.dialogService.open(BcAlertComponent, {
            id: 'errorAlert',
            type: 'error',
            title: 'Error',
            text: textAlert,
            timeout: Numbers._8000,
          })
        }
      });
  }

  handleErrorRequestHelper(event: number): void {
    if (event) {
      this.logOut();
      this.modalErrorHelper.closeModal();
    }
  }

  logOut: Function = () => {
    localStorage.clear();
    this.authService.logout({
      postLogoutRedirectUri: window.location.origin, // Redirige a la raíz
    });
  };

  ngOnDestroy() {
    this.spinnerService.clear();
    clearInterval(this.interval2);
  }

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent) {
    localStorage.clear();
    // Cambiar la URL a raíz antes de que se recargue
    this.router.navigate(["/"])
    window.history.pushState({}, '', '/');
  }

}
